import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  if (!token) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
            <div className="w-full max-w-md bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
                <p className="text-red-600 font-bold mb-4">Invalid Reset Link</p>
                <p className="text-sm text-gray-500 mb-6">No token was provided in the URL.</p>
                <Link to="/forgot-password" className="inline-block w-full py-2.5 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-all">Request New Link</Link>
            </div>
        </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    setStatus('loading');
    setMessage('');
    
    try {
      const res = await authService.resetPassword(token, password);
      setStatus('success');
      setMessage(res.message);
      setTimeout(() => navigate('/login'), 3500);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'Failed to reset password. The link may have expired.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
        <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">Choose New Password</h2>
        <p className="text-sm text-gray-500 mb-6">Enter your new password below.</p>

        {status === 'success' ? (
            <div className="py-4">
                <div className="p-4 bg-green-50 text-green-800 border border-green-200 rounded-lg mb-6 text-sm font-medium">
                    {message} Redirecting to login...
                </div>
            </div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-left">
                {status === 'error' && (
                    <div className="p-3 bg-red-50 text-red-600 text-sm rounded border border-red-100">
                        {message}
                    </div>
                )}
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">New Password (min 8 chars)</label>
                    <input 
                        type="password" 
                        required
                        minLength={8}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none transition"
                        value={password} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Confirm New Password</label>
                    <input 
                        type="password" 
                        required
                        minLength={8}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none transition"
                        value={confirmPassword} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    />
                </div>
                <button 
                    type="submit" 
                    disabled={status === 'loading'}
                    className={`w-full py-2.5 rounded-lg text-white font-bold transition mt-2 ${status === 'loading' ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 shadow-md'}`}
                >
                    {status === 'loading' ? 'Resetting Password...' : 'Reset Password'}
                </button>
            </form>
        )}
      </div>
    </div>
  );
}